// Simulator für Schaltnetze -- Gegenstück zu gatelab/circuit.py und gatelab/tasks.py:check.
// Beide müssen tests/fixtures/circuits.json bestehen (siehe sim.test.ts).

export type Bit = 0 | 1;

export const GATE_ARITY: Record<string, number> = {
  NOT: 1, AND: 2, OR: 2, NAND: 2, NOR: 2, XOR: 2, XNOR: 2, HA: 2, VA: 3,
};

/** Bausteine mit mehreren benannten Ausgängen (Quelle `<id>.<ausgang>`); alle anderen haben einen. */
export const GATE_OUTPUTS: Record<string, string[]> = { HA: ["S", "C"], VA: ["S", "C"] };

export interface Gate { id: string; type: string; x?: number; y?: number }
export interface Wire { from: string; to: string }
export interface Circuit { gates: Gate[]; wires: Wire[] }

export interface Task {
  id: string;
  titel: string;
  text: string;
  eingaenge: string[];
  ausgaenge: string[];
  ziel: Record<string, string>;
  tabelle: Bit[][];
  bauteile: Record<string, number | "*">;
  par: number | null;
  hinweise: string[];
  konstanten?: Bit[]; // feste Quellen "0"/"1", nicht Teil der Wahrheitstabelle
  exkurs?: boolean; // geht über den Kernstoff hinaus, wird als "Exkurs" markiert
}

export interface Problem { code: string; message: string; ref: string | null }

export interface CheckResult {
  solved: boolean;
  problems: Problem[];
  tabelle: Bit[][] | null;
  falsche_zeilen: number[];
  gatter: number;
  par_erreicht: boolean;
}

/** Ausgangswerte nach Ausgangsname; einfache Gatter haben den Namen "". */
export function evalOutputs(type: string, args: Bit[]): Record<string, Bit> {
  if (type === "HA") return { S: (args[0] ^ args[1]) as Bit, C: (args[0] & args[1]) as Bit };
  if (type === "VA") {
    const [a, b, c] = args;
    return { S: (a ^ b ^ c) as Bit, C: ((a & b) | (c & (a ^ b))) as Bit };
  }
  return { "": evalGate(type, args) };
}

/** Quelle "g1" oder "g1.S" -> Gatter-ID. */
export const sourceGate = (src: string): string => src.split(".")[0];

function validSource(src: string, gates: Map<string, string>): boolean {
  const dot = src.indexOf(".");
  const type = gates.get(dot < 0 ? src : src.slice(0, dot));
  if (type === undefined) return false;
  const names = GATE_OUTPUTS[type];
  return names ? dot >= 0 && names.includes(src.slice(dot + 1)) : dot < 0;
}

export function evalGate(type: string, args: Bit[]): Bit {
  const [a, b] = args;
  switch (type) {
    case "NOT": return (1 - a) as Bit;
    case "AND": return (a & b) as Bit;
    case "NAND": return (1 - (a & b)) as Bit;
    case "OR": return (a | b) as Bit;
    case "NOR": return (1 - (a | b)) as Bit;
    case "XOR": return (a ^ b) as Bit;
    case "XNOR": return (1 - (a ^ b)) as Bit;
  }
  throw new Error(`Unbekannter Gattertyp ${type}`);
}

/** Alle 2^n Belegungen; der erste Eingang ist das höchstwertige Bit. */
export function inputRows(inputs: string[]): Record<string, Bit>[] {
  const n = inputs.length;
  const rows: Record<string, Bit>[] = [];
  for (let i = 0; i < 1 << n; i++) {
    const row: Record<string, Bit> = {};
    inputs.forEach((name, k) => { row[name] = ((i >> (n - 1 - k)) & 1) as Bit; });
    rows.push(row);
  }
  return rows;
}

/** Senke -> Quelle; ungültige Leitungen werden übersprungen. */
export function driverMap(circuit: Circuit): Map<string, string> {
  const m = new Map<string, string>();
  for (const w of circuit.wires) if (!m.has(w.to)) m.set(w.to, w.from);
  return m;
}

interface SimResult {
  problems: Problem[];
  outputs: Record<string, Bit>[] | null;
  usedGates: string[];
}

export function simulate(circuit: Circuit, inputs: string[], outputs: string[], constants: Bit[] = []): SimResult {
  const problems: Problem[] = [];
  const res: SimResult = { problems, outputs: null, usedGates: [] };
  const fixed = new Map<string, Bit>(constants.map(c => [String(c), c])); // Konstanten-Quellen
  const p = (code: string, message: string, ref: string | null) => problems.push({ code, message, ref });

  const gates = new Map<string, string>();
  for (const g of circuit.gates) {
    const id = String(g.id ?? "");
    const type = String(g.type ?? "").toUpperCase();
    if (!id || id.includes(".") || inputs.includes(id) || outputs.includes(id) || fixed.has(id)) {
      p("bad_id", `Ungültige Gatter-ID '${id}'`, id);
    } else if (gates.has(id)) {
      p("duplicate_id", `Gatter-ID '${id}' doppelt`, id);
    } else if (!(type in GATE_ARITY)) {
      p("unknown_gate_type", `Unbekannter Gattertyp '${type}'`, id);
    } else {
      gates.set(id, type);
    }
  }

  const driver = new Map<string, string>();
  for (const w of circuit.wires) {
    const src = String(w.from ?? ""), dst = String(w.to ?? "");
    if (!inputs.includes(src) && !fixed.has(src) && !validSource(src, gates)) {
      p("unknown_source", `Unbekannte Quelle '${src}'`, src);
      continue;
    }
    if (!outputs.includes(dst)) {
      const dot = dst.indexOf(".");
      const gid = dot < 0 ? dst : dst.slice(0, dot);
      const pin = dot < 0 ? "" : dst.slice(dot + 1);
      if (!gates.has(gid) || !/^\d+$/.test(pin) || Number(pin) >= GATE_ARITY[gates.get(gid)!]) {
        p("unknown_sink", `Unbekanntes Ziel '${dst}'`, dst);
        continue;
      }
    }
    if (driver.has(dst) && driver.get(dst) !== src) {
      p("multiple_drivers", `'${dst}' wird von mehreren Leitungen getrieben`, dst);
      continue;
    }
    driver.set(dst, src);
  }
  if (problems.length) return res;

  const state = new Map<string, number>(); // 1 = in Bearbeitung, 2 = fertig
  const order: string[] = [];
  const visit = (src: string): void => {
    if (inputs.includes(src) || fixed.has(src)) return;
    const gid = sourceGate(src);
    if (state.get(gid) === 2) return;
    if (state.get(gid) === 1) {
      p("cycle", "Rückkopplung (Schleife) ist nicht erlaubt", gid);
      return;
    }
    state.set(gid, 1);
    for (let k = 0; k < GATE_ARITY[gates.get(gid)!]; k++) {
      const pin = `${gid}.${k}`;
      const d = driver.get(pin);
      if (d === undefined) p("open_input", `Eingang ${k + 1} von ${gid} ist offen`, pin);
      else visit(d);
    }
    state.set(gid, 2);
    order.push(gid);
  };
  for (const out of outputs) {
    const d = driver.get(out);
    if (d === undefined) p("open_output", `Ausgang ${out} ist nicht angeschlossen`, out);
    else visit(d);
  }
  res.usedGates = order;
  if (problems.length) return res;

  res.outputs = inputRows(inputs).map(env => {
    const values = new Map<string, Bit>([...Object.entries(env), ...fixed]);
    for (const gid of order) {
      const type = gates.get(gid)!;
      const args: Bit[] = [];
      for (let k = 0; k < GATE_ARITY[type]; k++) args.push(values.get(driver.get(`${gid}.${k}`)!)!);
      for (const [out, v] of Object.entries(evalOutputs(type, args))) values.set(out ? `${gid}.${out}` : gid, v);
    }
    const row: Record<string, Bit> = {};
    for (const out of outputs) row[out] = values.get(driver.get(out)!)!;
    return row;
  });
  return res;
}

export function gateCounts(circuit: Circuit): Map<string, number> {
  const m = new Map<string, number>();
  for (const g of circuit.gates) {
    const t = String(g.type).toUpperCase();
    m.set(t, (m.get(t) ?? 0) + 1);
  }
  return m;
}

export function check(task: Task, circuit: Circuit): CheckResult {
  const problems: Problem[] = [];
  for (const [type, n] of gateCounts(circuit)) {
    const limit = task.bauteile[type];
    if (limit === undefined) {
      problems.push({ code: "gate_not_allowed", ref: type, message: `${type} ist in dieser Aufgabe nicht erlaubt` });
    } else if (limit !== "*" && n > limit) {
      problems.push({ code: "too_many_gates", ref: type, message: `Höchstens ${limit}× ${type} erlaubt` });
    }
  }
  const sim = simulate(circuit, task.eingaenge, task.ausgaenge, task.konstanten ?? []);
  problems.push(...sim.problems);
  const result: CheckResult = {
    solved: false, problems, tabelle: null, falsche_zeilen: [],
    gatter: sim.usedGates.length, par_erreicht: false,
  };
  if (!sim.outputs) return result;

  const tabelle = sim.outputs.map(row => task.ausgaenge.map(o => row[o]));
  const falsch: number[] = [];
  tabelle.forEach((ist, i) => {
    if (ist.some((v, k) => v !== task.tabelle[i][k])) falsch.push(i);
  });
  result.tabelle = tabelle;
  result.falsche_zeilen = falsch;
  result.solved = problems.length === 0 && falsch.length === 0;
  result.par_erreicht = result.solved && (task.par === null || result.gatter <= task.par);
  return result;
}

/**
 * Live-Werte aller Signale für eine Eingangsbelegung (für die Leitungsfarben im Editor).
 * Tolerant: Signale hinter offenen Eingängen oder in Schleifen bleiben undefined.
 */
export function liveValues(circuit: Circuit, env: Record<string, Bit>): Map<string, Bit> {
  const types = new Map(circuit.gates.map(g => [g.id, g.type.toUpperCase()]));
  const driver = driverMap(circuit);
  const values = new Map<string, Bit>(Object.entries(env));
  const busy = new Set<string>();
  const value = (src: string): Bit | undefined => {
    if (values.has(src)) return values.get(src);
    const gid = sourceGate(src);
    const type = types.get(gid);
    if (type === undefined || busy.has(gid)) return undefined;
    busy.add(gid);
    const args: Bit[] = [];
    for (let k = 0; k < GATE_ARITY[type]; k++) {
      const d = driver.get(`${gid}.${k}`);
      const v = d === undefined ? undefined : value(d);
      if (v === undefined) { busy.delete(gid); return undefined; }
      args.push(v);
    }
    busy.delete(gid);
    for (const [out, v] of Object.entries(evalOutputs(type, args))) values.set(out ? `${gid}.${out}` : gid, v);
    return values.get(src);
  };
  for (const id of types.keys()) value(id);
  return values;
}
